import { useState, useCallback, useEffect } from 'react'
import {
  VersionReportPageType,
  VersionReportPageConfig,
  ProgressOverviewPageConfig,
  ProgressReportByProcessPageConfig,
  ProgressReportByTeamPageConfig,
  VersionReportTemplatePageEntity,
} from '../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { AggregateField } from '../../../../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../../../../lib/functions/workload'
import { generateUuid } from '../../../../../../../utils/uuids'
import { VersionReportTemplatePageFormModel } from '../model/VersionReportTemplatePageFormModel'

export const PAGE_TYPE_LABELS = {
  PROGRESS_OVERVIEW: '進捗概況',
  PROGRESS_REPORT_BY_PROCESS: '工程別進捗状況',
  PROGRESS_REPORT_BY_TEAM: 'チーム別進捗状況',
} as const

export type PageTypeLabel =
  (typeof PAGE_TYPE_LABELS)[keyof typeof PAGE_TYPE_LABELS]

export const PAGE_TYPE_MAPPING = {
  [PAGE_TYPE_LABELS.PROGRESS_OVERVIEW]: 'PROGRESS_OVERVIEW',
  [PAGE_TYPE_LABELS.PROGRESS_REPORT_BY_PROCESS]: 'PROGRESS_REPORT_BY_PROCESS',
  [PAGE_TYPE_LABELS.PROGRESS_REPORT_BY_TEAM]: 'PROGRESS_REPORT_BY_TEAM',
} as const satisfies Record<PageTypeLabel, VersionReportPageType>

const createDefaultConfig = (
  pageType: VersionReportPageType
): VersionReportPageConfig => {
  switch (pageType) {
    case 'PROGRESS_OVERVIEW':
      return {
        type: 'PROGRESS_OVERVIEW',
        targetUuids: [],
      } as ProgressOverviewPageConfig

    case 'PROGRESS_REPORT_BY_PROCESS':
      return {
        type: 'PROGRESS_REPORT_BY_PROCESS',
        tableRows: [],
        aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
        aggregateTarget: 'TASK',
        workloadUnit: WorkloadUnit.HOUR,
        columnState: [],
        rowHeight: undefined,
      } as ProgressReportByProcessPageConfig

    case 'PROGRESS_REPORT_BY_TEAM':
      return {
        type: 'PROGRESS_REPORT_BY_TEAM',
        rootUuid: undefined,
        tableRows: [],
      } as ProgressReportByTeamPageConfig

    default:
      throw new Error(`Unsupported page type: ${pageType}`)
  }
}

const reassignPageNumbers = (
  pages: VersionReportTemplatePageFormModel[]
): Map<number, VersionReportTemplatePageFormModel> => {
  const reorderedMap = new Map<number, VersionReportTemplatePageFormModel>()
  pages.forEach((entity, index) => {
    const newPageNumber = index + 1
    const reorderedEntity = { ...entity, pageNumber: newPageNumber }
    reorderedMap.set(newPageNumber, reorderedEntity)
  })
  return reorderedMap
}

const calculateNextSelectedPage = (
  currentSelection: number | null,
  deletedPageNumber: number,
  remainingPagesCount: number
): number | null => {
  if (remainingPagesCount === 0) {
    return null
  }

  if (currentSelection !== deletedPageNumber) {
    if (currentSelection! < deletedPageNumber) {
      return currentSelection
    }
    return currentSelection! - 1
  }

  return Math.min(deletedPageNumber, remainingPagesCount)
}

export const usePageEntityMap = (
  initialPages?: VersionReportTemplatePageEntity[]
) => {
  const [pageEntityMap, setPageEntityMap] = useState<
    Map<number, VersionReportTemplatePageFormModel>
  >(new Map())
  const [nextPageNumber, setNextPageNumber] = useState(1)
  const [currentSelectedPageNumber, setCurrentSelectedPageNumber] = useState<
    number | null
  >(null)
  const [lastInitialPages, setLastInitialPages] = useState<
    VersionReportTemplatePageFormModel[] | undefined
  >()

  useEffect(() => {
    if (
      initialPages &&
      initialPages.length > 0 &&
      initialPages !== lastInitialPages
    ) {
      const map = new Map<number, VersionReportTemplatePageFormModel>()
      initialPages.forEach(page => {
        map.set(page.pageNumber, page as VersionReportTemplatePageFormModel)
      })
      setPageEntityMap(map)
      setNextPageNumber(Math.max(...initialPages.map(p => p.pageNumber)) + 1)
      setCurrentSelectedPageNumber(1)
      setLastInitialPages(initialPages)
    } else if (!initialPages && lastInitialPages !== undefined) {
      setLastInitialPages(undefined)
    }
  }, [initialPages, lastInitialPages])

  const currentPageEntity = currentSelectedPageNumber
    ? pageEntityMap.get(currentSelectedPageNumber)
    : null

  const addPageEntity = useCallback(
    (pageTypeLabel: PageTypeLabel) => {
      const pageType = PAGE_TYPE_MAPPING[pageTypeLabel]
      const defaultConfig = createDefaultConfig(pageType)

      const newPageEntity: VersionReportTemplatePageFormModel = {
        pageTitle: pageTypeLabel,
        pageNumber: nextPageNumber,
        pageType,
        config: defaultConfig,
      }

      setPageEntityMap(prev => new Map(prev.set(nextPageNumber, newPageEntity)))
      setCurrentSelectedPageNumber(nextPageNumber)
      setNextPageNumber(prev => prev + 1)
    },
    [nextPageNumber]
  )

  const updatePageEntity = useCallback(
    (
      pageNumber: number,
      updater: (
        prev: VersionReportTemplatePageFormModel
      ) => VersionReportTemplatePageFormModel
    ) => {
      setPageEntityMap(prev => {
        const entity = prev.get(pageNumber)
        if (!entity) return prev

        const updated = updater(entity)
        const newMap = new Map(prev)
        newMap.set(pageNumber, updated)
        return newMap
      })
    },
    []
  )

  const deletePageEntity = useCallback(
    (pageNumber: number) => {
      setPageEntityMap(prev => {
        const entity = prev.get(pageNumber)
        if (!entity) return prev

        const newMap = new Map(prev)
        newMap.delete(pageNumber)

        if (newMap.size === 0) {
          setCurrentSelectedPageNumber(null)
          setNextPageNumber(1)
          return new Map()
        }

        const sortedPages = Array.from(newMap.values()).sort(
          (a, b) => a.pageNumber - b.pageNumber
        )
        const reorderedMap = reassignPageNumbers(sortedPages)

        const newSelection = calculateNextSelectedPage(
          currentSelectedPageNumber,
          pageNumber,
          reorderedMap.size
        )
        setCurrentSelectedPageNumber(newSelection)
        setNextPageNumber(reorderedMap.size + 1)

        return reorderedMap
      })
    },
    [currentSelectedPageNumber]
  )

  const reorderPages = useCallback(
    (reorderedPages: VersionReportTemplatePageFormModel[]) => {
      const reorderedMap = reassignPageNumbers(reorderedPages)
      setPageEntityMap(reorderedMap)

      if (currentSelectedPageNumber) {
        const currentEntity = pageEntityMap.get(currentSelectedPageNumber)
        if (currentEntity) {
          const newPageNumber =
            reorderedPages.findIndex(page => page === currentEntity) + 1

          if (newPageNumber > 0) {
            setCurrentSelectedPageNumber(newPageNumber)
          }
        }
      }
    },
    [currentSelectedPageNumber, pageEntityMap]
  )

  const resetPageEntityMap = useCallback(() => {
    setPageEntityMap(new Map())
    setNextPageNumber(1)
    setCurrentSelectedPageNumber(null)
    setLastInitialPages(undefined)
  }, [])

  return {
    pageEntityMap,
    currentPageEntity,
    currentSelectedPageNumber,
    setCurrentSelectedPageNumber,
    addPageEntity,
    updatePageEntity,
    deletePageEntity,
    reorderPages,
    resetPageEntityMap,
  }
}
